import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SEO from '../components/common/SEO';
import Breadcrumb from '../components/common/Breadcrumb';
import SafeIcon from '../components/common/SafeIcon';
import PokemonGrid from '../components/pokemon/PokemonGrid';
import { pokemonData } from '../data/pokemonData';

const { FiChevronLeft, FiChevronRight } = FiIcons;

function AllPokemonPage() {
  const [currentPage, setCurrentPage] = useState(1);
  const [pokemonPerPage] = useState(24);
  const [sortBy, setSortBy] = useState('id');
  const [sortedPokemon, setSortedPokemon] = useState([]);

  useEffect(() => {
    let sorted = [...pokemonData];
    
    switch (sortBy) {
      case 'name':
        sorted.sort((a, b) => a.displayName.localeCompare(b.displayName));
        break;
      case 'id':
        sorted.sort((a, b) => a.id - b.id);
        break;
      case 'hp':
        sorted.sort((a, b) => b.baseStats.hp - a.baseStats.hp);
        break;
      case 'attack':
        sorted.sort((a, b) => b.baseStats.attack - a.baseStats.attack);
        break;
      case 'defense':
        sorted.sort((a, b) => b.baseStats.defense - a.baseStats.defense);
        break;
      case 'speed':
        sorted.sort((a, b) => b.baseStats.speed - a.baseStats.speed);
        break;
      default:
        break;
    }
    
    setSortedPokemon(sorted);
    setCurrentPage(1);
  }, [sortBy]);

  const totalPages = Math.ceil(sortedPokemon.length / pokemonPerPage);
  const startIndex = (currentPage - 1) * pokemonPerPage;
  const endIndex = startIndex + pokemonPerPage;
  const currentPokemon = sortedPokemon.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const breadcrumbItems = [
    { name: 'All Pokemon' }
  ];

  const pageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "All Pokemon - Complete Pokemon Database",
    "description": `Browse all ${pokemonData.length} Pokemon with detailed stats, abilities, and information. Page ${currentPage} of ${totalPages}.`,
    "url": "https://example.com/pokemon",
    "isPartOf": {
      "@type": "WebSite",
      "name": "Pokemon Stats Database",
      "url": "https://example.com"
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen bg-gray-50 py-8"
    >
      <SEO
        title={`All Pokemon - Complete Database (Page ${currentPage})`}
        description={`Browse all ${pokemonData.length} Pokemon with detailed stats, abilities, and information. Complete Pokemon reference database with search and filtering.`}
        keywords="all pokemon, pokemon list, pokemon database, pokemon stats, complete pokemon guide"
        url="/pokemon"
        schema={pageSchema}
      />

      <div className="container mx-auto px-4">
        <Breadcrumb items={breadcrumbItems} />

        {/* Header */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="mb-8"
        >
          <h1 className="text-4xl font-bold text-gray-900 mb-4">All Pokemon</h1>
          <p className="text-xl text-gray-600">
            Browse our complete database of {pokemonData.length} Pokemon
          </p>
        </motion.div>

        {/* Controls */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-lg shadow-md p-6 mb-8"
        >
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
            <div className="flex items-center space-x-4">
              <label htmlFor="sort-select" className="text-sm font-medium text-gray-700">
                Sort by:
              </label>
              <select
                id="sort-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="id">Pokedex Number</option>
                <option value="name">Name (A-Z)</option>
                <option value="hp">Highest HP</option>
                <option value="attack">Highest Attack</option>
                <option value="defense">Highest Defense</option>
                <option value="speed">Highest Speed</option>
              </select>
            </div>

            <div className="text-sm text-gray-600">
              Showing {startIndex + 1}-{Math.min(endIndex, sortedPokemon.length)} of {sortedPokemon.length} Pokemon
            </div>
          </div>
        </motion.div>

        {/* Pokemon Grid */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <PokemonGrid pokemon={currentPokemon} />
        </motion.div>

        {/* Pagination */}
        {totalPages > 1 && (
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="mt-12 flex justify-center"
          >
            <nav className="flex items-center space-x-2" aria-label="Pagination">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-2 rounded-md bg-white border border-gray-300 text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed focus:ring-2 focus:ring-blue-500"
                aria-label="Previous page"
              >
                <SafeIcon icon={FiChevronLeft} className="w-5 h-5" />
              </button>

              {/* Page Numbers */}
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`px-4 py-2 rounded-md font-medium focus:ring-2 focus:ring-blue-500 ${
                      currentPage === pageNum
                        ? 'bg-blue-600 text-white'
                        : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                    aria-label={`Go to page ${pageNum}`}
                    aria-current={currentPage === pageNum ? 'page' : undefined}
                  >
                    {pageNum}
                  </button>
                );
              })}

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="p-2 rounded-md bg-white border border-gray-300 text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed focus:ring-2 focus:ring-blue-500"
                aria-label="Next page"
              >
                <SafeIcon icon={FiChevronRight} className="w-5 h-5" />
              </button>
            </nav>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}

export default AllPokemonPage;